/**
 * ActiveFilters — removable chip bar displayed above the events table.
 * Mirrors the chip-generation pattern from app-fraud-score-checker.
 */

import CloseIcon from "@mui/icons-material/Close";
import { Box, Chip } from "@mui/material";
import { colors } from "@/theme/eventsTokens.ts";
import type { EventFilters } from "@/types";
import { isPrefixMode } from "@/utils/ipSearch.ts";

interface ActiveFiltersProps {
  readonly filters: EventFilters;
  readonly onRemoveFilter: (
    key: keyof EventFilters,
    value?: string,
  ) => void;
}

interface FilterChip {
  label: string;
  key: keyof EventFilters;
  value?: string;
}

function buildDateChips(filters: EventFilters): FilterChip[] {
  if (!filters.start_date && !filters.end_date) return [];
  return [{
    label: `Date: ${filters.start_date ?? "…"} – ${filters.end_date ?? "…"}`,
    key: "start_date",
  }];
}

function buildScoreChips(filters: EventFilters): FilterChip[] {
  const hasMin = filters.min_score !== undefined && filters.min_score > 0;
  const hasMax = filters.max_score !== undefined && filters.max_score < 100;
  if (!hasMin && !hasMax) return [];
  return [{
    label: `Score: ${filters.min_score ?? 0}–${filters.max_score ?? 100}`,
    key: "min_score",
  }];
}

function buildIpChips(filters: EventFilters): FilterChip[] {
  if (!filters.ip_search) return [];
  const label = isPrefixMode(filters.ip_search)
    ? `IP starts with: ${filters.ip_search}`
    : `IP: ${filters.ip_search}`;
  return [{ label, key: "ip_search" }];
}

function buildArrayChips(
  values: string[] | undefined,
  prefix: string,
  key: keyof EventFilters,
): FilterChip[] {
  if (!values?.length) return [];
  return values.map((v) => ({ label: `${prefix}: ${v}`, key, value: v }));
}

const TRI_STATE_LABELS: Record<string, { with: string; without: string }> = {
  has_blocked: { with: "Has Blocked Attempts", without: "No Blocked Attempts" },
  has_failures: { with: "Has Authentication Failures", without: "No Authentication Failures" },
  has_successful_auth: { with: "Has Successful Authentication", without: "No Successful Authentication" },
  has_user_not_found: { with: "Has User Not Found", without: "No User Not Found" },
  has_invalid_password: { with: "Has Invalid Password", without: "No Invalid Password" },
  has_failed_second_factor: { with: "Has Failed Second Factor", without: "No Failed Second Factor" },
};

function buildTriStateChips(filters: EventFilters): FilterChip[] {
  const chips: FilterChip[] = [];
  for (const [key, labels] of Object.entries(TRI_STATE_LABELS)) {
    const value = filters[key as keyof EventFilters] as string | undefined;
    if (value) {
      chips.push({
        label: value === "with" ? labels.with : labels.without,
        key: key as keyof EventFilters,
      });
    }
  }
  return chips;
}

function buildChips(filters: EventFilters): FilterChip[] {
  return [
    ...buildDateChips(filters),
    ...buildScoreChips(filters),
    ...buildIpChips(filters),
    ...buildArrayChips(filters.country_codes, "Country", "country_codes"),
    ...buildArrayChips(filters.asns, "ASN", "asns"),
    ...buildTriStateChips(filters),
  ];
}

function FilterChipItem({
  chip,
  onRemove,
}: {
  readonly chip: FilterChip;
  readonly onRemove: () => void;
}) {
  return (
    <Chip
      data-testid={`filter-chip-${chip.key}`}
      label={chip.label}
      size="small"
      deleteIcon={<CloseIcon data-testid="chip-delete" sx={{ fontSize: "14px !important" }} />}
      onDelete={onRemove}
      sx={{
        backgroundColor: colors.backgroundMuted,
        border: "none",
        fontWeight: 500,
        fontSize: "0.75rem",
        height: "26px",
        color: colors.textSecondary,
        "& .MuiChip-deleteIcon": {
          color: colors.textMuted,
          "&:hover": { color: colors.textPrimary },
        },
      }}
    />
  );
}

export default function ActiveFilters({
  filters,
  onRemoveFilter,
}: ActiveFiltersProps) {
  const chips = buildChips(filters);

  return (
    <Box
      data-testid="active-filters"
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 1,
        minHeight: 36,
        mb: chips.length > 0 ? 1 : 0,
      }}
    >
      {chips.map((chip, index) => (
        <FilterChipItem
          key={`${chip.key}-${chip.value ?? ""}-${index}`}
          chip={chip}
          onRemove={() => onRemoveFilter(chip.key, chip.value)}
        />
      ))}
    </Box>
  );
}
